/**
 * One chosen workbook in the loan tape onboarding (DOP-627): what it is for,
 * its name, the deal it sits in, and Change. With nothing chosen the card is
 * the search that chooses it.
 */

import { FileSpreadsheet, Table2 } from "lucide-react";
import React, { useState } from "react";
import type { SchemaExtractorDocRef } from "../SchemaExtractorForm";
import type { CatalogDocument } from "../../hooks/useDocumentMention";
import WorkbookSearch from "./WorkbookSearch";

export interface WorkbookCardProps {
	/** "The raw file", "Your finished tape". */
	kind: string;
	icon: "raw" | "tape";
	value: SchemaExtractorDocRef | null;
	onChange: (doc: CatalogDocument) => void;
	exclude?: readonly string[];
}

const WorkbookCard: React.FC<WorkbookCardProps> = ({
	kind,
	icon,
	value,
	onChange,
	exclude,
}) => {
	const [picking, setPicking] = useState(false);
	const Icon = icon === "raw" ? FileSpreadsheet : Table2;

	if (picking) {
		return (
			<div className="min-w-0 flex-1">
				<p className="mb-1.5 text-[11.5px] font-semibold uppercase tracking-[0.05em] text-[#6B7280]">
					{kind}
				</p>
				<WorkbookSearch
					label={`Find ${kind.toLowerCase()}`}
					exclude={exclude}
					autoFocus
					onPick={(doc) => {
						onChange(doc);
						setPicking(false);
					}}
					onCancel={() => setPicking(false)}
				/>
			</div>
		);
	}

	if (!value) {
		return (
			<button
				type="button"
				onClick={() => setPicking(true)}
				className="flex min-w-0 flex-1 items-center gap-3 rounded-xl border-[1.5px] border-dashed border-[#d0d5dd] bg-white px-4 py-3.5 text-left hover:border-[#99CFCD] hover:bg-[#f6fbfb] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#008782]"
			>
				<span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-[#f7f7f6] text-[#9a9ea3]">
					<Icon className="h-[18px] w-[18px]" aria-hidden />
				</span>
				<span className="min-w-0 flex-1">
					<span className="block text-[11.5px] font-semibold uppercase tracking-[0.05em] text-[#6B7280]">
						{kind}
					</span>
					<span className="mt-0.5 block text-sm font-medium text-[#004C4B]">
						Choose from the Deal Room
					</span>
				</span>
			</button>
		);
	}

	return (
		<div className="flex min-w-0 flex-1 items-center gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3.5">
			<span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-[#ecfdf3] text-[#067647]">
				<Icon className="h-[18px] w-[18px]" aria-hidden />
			</span>
			<div className="min-w-0 flex-1">
				<p className="text-[11.5px] font-semibold uppercase tracking-[0.05em] text-[#6B7280]">
					{kind}
				</p>
				<p className="mt-0.5 text-sm font-semibold text-[#111111] [overflow-wrap:anywhere]">
					{value.filename ?? value.doc_id}
				</p>
				{value.deal_name && (
					<p className="mt-0.5 truncate text-xs text-[#6B7280]">
						in {value.deal_name}
					</p>
				)}
			</div>
			<button
				type="button"
				onClick={() => setPicking(true)}
				aria-label={`Change ${kind.toLowerCase()}`}
				className="p-1 text-[12.5px] font-medium text-[#004C4B] underline underline-offset-[3px]"
			>
				Change
			</button>
		</div>
	);
};

export default WorkbookCard;
